import { useMutation } from '@apollo/client';
import React, { FC, useCallback, useState } from 'react';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import Form, { ErrorMessage, Field, FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import TextArea from '@atlaskit/textarea';
import { ApprovalStatus, BulkTaskUpdateMutation, Task } from '@css-xp/apd-common';
import { ErrorComponents } from '@css-xp/base';
import { useApplicationContext } from '@css-xp/base/common-context';
import { ModalTitle } from '../constants/modal-titles';
import { bulkTaskUpdate as BULK_UPDATE_TASK } from '../queries/tasks';
import ErrorNotificationMessage from './error-notification-message';
import './modal.scss';
import { useModalNotification } from './use-modal-notification';

interface RequestModalProps {
  taskList: Task[];
  onComplete?: () => void;
  closeModal: () => void;
}

const BulkApproveModal: FC<RequestModalProps> = ({ taskList, onComplete, closeModal }) => {
  const [loading, setLoading] = useState<boolean>(false);

  const [updateTask] = useMutation<BulkTaskUpdateMutation>(BULK_UPDATE_TASK);

  const userProfile = useApplicationContext();

  const [renderSuccessNotification, renderErrorNotification] = useModalNotification(
    closeModal,
    'bulk-approve-modal-notification'
  );

  const handleFormSubmit = useCallback(
    async ({ comment }: { comment: string }) => {
      setLoading(true);
      const userID = userProfile?.userId?.split('@')?.[0];
      try {
        const taskIds = taskList.map((task: Task) => task.taskId);
        const result = await updateTask({
          variables: {
            input: {
              taskIds,
              approvalStatus: ApprovalStatus.APPROVED,
              comment,
            },
            id: userID,
          },
        });
        const errorMsg = result?.errors?.[0]?.extensions?.message;
        if (errorMsg) {
          throw new Error(errorMsg);
        }
        const status = result?.data?.bulkTaskUpdate?.status ?? '';

        if (status === 'OK') {
          const taskStatusUpdatedList = result?.data?.bulkTaskUpdate?.taskList?.filter((item) => item?.updated) ?? [];
          if (!taskStatusUpdatedList.length) {
            //All requests failed
            renderErrorNotification(
              'We’re unable to process the changes',
              <>
                Having trouble? Contact the <ErrorComponents.ChatRoomLink /> channel.
              </>
            );
          } else {
            onComplete?.();
            if (taskStatusUpdatedList.length === result?.data?.bulkTaskUpdate?.taskList?.length) {
              //All requests updated succesfully
              renderSuccessNotification('Action complete', 'You approved the selected requests.');
            } else {
              //Partial requests updated succesfully
              renderErrorNotification(
                'We’re unable to process some changes',
                <>
                  <p>Notifications have been added to the relevant requests in Slack.</p>
                  <p>
                    Still having trouble? Contact the <ErrorComponents.ChatRoomLink />.
                  </p>
                </>
              );
            }
          }
        }
      } catch (e: any) {
        renderErrorNotification(
          'We’re unable to approve the requests',
          <ErrorNotificationMessage errorMessage={e.message} />
        );
      } finally {
        setLoading(false);
      }
    },
    [taskList]
  );

  return (
    <Form onSubmit={handleFormSubmit}>
      {({ formProps }) => (
        <div className="modal-form-wrapper" data-testid="bulk-approve-modal">
          <form {...formProps}>
            <FormHeader
              title={
                <div className="modal-title-wrapper">
                  <span className="modal-title">{ModalTitle.APPROVE}</span>
                </div>
              }
            />
            <p>
              Your decision will be added to the selected requests in the relevant Slack channels and Approval Desk.
              Once you approve, the requested changes will be applied.
            </p>
            <FormSection>
              <Field<string, HTMLTextAreaElement>
                name="comment"
                label="Internal comment"
                isRequired={true}
                validate={(value: any) => (value && value.trim().length > 0 ? undefined : 'INVALID')}
              >
                {({ fieldProps, error }) => (
                  <>
                    <TextArea {...fieldProps} placeholder="Add comment" testId="comment-input" isInvalid={!!error} />
                    {error && <ErrorMessage testId="comment-input-error">This field is required</ErrorMessage>}
                  </>
                )}
              </Field>
            </FormSection>
            <FormFooter align="end">
              <ButtonGroup>
                <Button appearance="subtle" onClick={closeModal} testId="cancel-btn">
                  Cancel
                </Button>
                <LoadingButton appearance="primary" autoFocus type="submit" isLoading={loading} testId="submit-btn">
                  Confirm
                </LoadingButton>
              </ButtonGroup>
            </FormFooter>
          </form>
        </div>
      )}
    </Form>
  );
};

export default BulkApproveModal;
